// WysiwygEditor component
import React, { Component } from 'react'
import ReactDom from 'react-dom'
import PropTypes from 'prop-types'
import $ from 'jquery'
import set from 'lodash/set'
import classnames from 'classnames'
import BoldIcon from '../../../svgicons/icon-bold.svg'
import UnderlineIcon from '../../../svgicons/icon-underline.svg'
import ItalicIcon from '../../../svgicons/icon-italic.svg'
import OutdentIcon from '../../../svgicons/icon-outdent.svg'
import IndentIcon from '../../../svgicons/icon-indent.svg'
import UploadFileIcon from '../../../svgicons/icon-upload-file.svg'

import 'at.js'
import 'froala-editor/js/froala_editor.min.js'

import FroalaEditorWysiwyg from 'react-froala-wysiwyg'
import IntlUtil from '../../../lib/intlutil.jsx'
import IntlMessage from '../../common/intl/intl_message.jsx'
import PanelDoc from '../../../theme/panel/panel_doc/panel_doc'
import { IntlProvider } from 'react-intl'
import AttachmentFile from '../../../theme/panel/panel_doc/attacthment_file'

const { froalaEditor } = require('../../../../config/default.json')

set(
  $,
  'FroalaEditor.DEFAULTS.key',
  froalaEditor.licenseKey
)

class WysiwygEditor extends Component {
  static get propTypes () {
    return {
      placeholder: PropTypes.string,
      value: PropTypes.any,
      onChange: PropTypes.func,
      onBlur: PropTypes.func,
      inlineEditor: PropTypes.bool,
      autoFocus: PropTypes.bool,
      error: PropTypes.string,
      handleCodeViewToggle: PropTypes.func,
      bShowLink: PropTypes.bool,
      hasAutocomplete: PropTypes.bool,
      saveSelection: PropTypes.bool,
      autocompleteChar: PropTypes.string,
      autocompleteOptions: PropTypes.array,
      onFocus: PropTypes.func,
      className: PropTypes.string,
      onKeyDown: PropTypes.func,
      isUpdatable: PropTypes.bool,
      fileUploadProps: PropTypes.shape({
        id: PropTypes.string,
        onUpload: PropTypes.func,
        onDelete: PropTypes.func,
        documents: PropTypes.array,
        baseDownloadUrl: PropTypes.string,
        uploadUrl: PropTypes.string,
        paramsToIncludeDuringUpload: PropTypes.object
      })
    }
  }

  static get defaultProps () {
    return {
      placeholder: '',
      value: '',
      onChange: null,
      onBlur: null,
      inlineEditor: false,
      autoFocus: false,
      error: '',
      handleCodeViewToggle: null,
      bShowLink: true,
      hasAutocomplete: false,
      saveSelection: false,
      autocompleteChar: '@',
      autocompleteOptions: [],
      onFocus: null,
      className: '',
      onKeyDown: null,
      isUpdatable: false,
      fileUploadProps: null
      // isUpdatable checkes if we want the editor to be updated in the componentDidUpdate life-cycle method.
      // We want to update it for e-mail templates (when we change the language and expect the right template to appear)
      // and we do not want it for the description field (or text will disappear when the component updates in another tab or device).
    }
  }

  constructor (props) {
    super(props)

    this.state = {
      value: props.value
    }

    this._references = {
      froalaEditor: React.createRef()
    }

    this._editorEl = null
  }

  componentDidUpdate (prevProps) {
    if (this.props.isUpdatable) {
      if (this.props.value !== prevProps.value) {
        this.setState({ value: this.props.value })
      }
    }
    this._focusEditor()
  }

  componentDidMount () {
    this._focusEditor()
  }

  componentWillUnmount () {
    if (this._editorEl) {
      this._editorEl.$el.off('inserted.atwho')
      this._editorEl.$el.off('keydown')
    }
  }

  render () {
    const { inlineEditor, fileUploadProps } = this.props
    const value = this.state.value
    const tag = inlineEditor ? '' : 'textarea'
    const _className = classnames('wysiwyg-editor', {}, this.props.className)
    return (
      <div className={_className}>
        <FroalaEditorWysiwyg
          config={this._getEditorConfig()}
          model={value}
          onModelChange={this._onChange.bind(this)}
          ref={this._references.froalaEditor}
          tag={tag}
        />
        {fileUploadProps?.documents?.map((item, idx) => {
          const href = [fileUploadProps.baseDownloadUrl || '', item.fileName].join('/')
          return (
            <AttachmentFile
              key={idx}
              href={href}
              name={item.fileDisplayName}
              onDelete={() => fileUploadProps?.onDelete(idx)}
            />
          )
        })}
        {this._renderError()}
      </div>
    )
  }

  focus () {
    if (this._references.froalaEditor.current) {
      this._references.froalaEditor.current.$editor.focus()
    }
  }

  insertHtml (html) {
    if (this._references.froalaEditor.current) {
      this._references.froalaEditor.current.$element.froalaEditor(
        'selection.restore'
      )
      this._references.froalaEditor.current.$element.froalaEditor(
        'html.insert',
        html,
        true
      )
    }
  }

  getValue () {
    let html = this.state.value
    if (this._editorEl && this._editorEl.html) {
      html = this._editorEl.html.get()
    }

    return html
  }

  /* Event handlers */
  _onBlur (event, _editor) {
    if (this.props.saveSelection && this._references.froalaEditor.current) {
      this._references.froalaEditor.current.$element.froalaEditor(
        'selection.save'
      )
    }

    if (this.props.onBlur) {
      let html = this.state.value
      if (this._editorEl && this._editorEl.html) {
        html = this._editorEl.html.get()
      }

      this.props.onBlur(html, event)
    }
  }

  _onChange (value) {
    this.setState({ value })
    if (this.props.onChange) {
      this.props.onChange(value)
    }
  }

  /* Private functions */
  _focusEditor () {
    if (this.props.autoFocus) {
      this.focus()
    }
  }

  _getEditorConfig () {
    let toolbarInline = true
    let toolbarButtons = [
      'bold',
      'italic',
      'underline',
      'undo',
      '-',
      'formatOL',
      'formatUL',
      'indent',
      'outdent',
      'redo'
    ]
    const customToolbarButtons = {
      underline: UnderlineIcon,
      bold: BoldIcon,
      italic: ItalicIcon,
      outdent: OutdentIcon,
      indent: IndentIcon,
      uploadFile: UploadFileIcon
    }
    let paragraphFormat = {}

    Object.keys(customToolbarButtons).forEach(buttonStr => {
      $.FroalaEditor.DefineIcon(buttonStr, {
        SRC: customToolbarButtons[buttonStr],
        template: 'image'
      })
    })

    if (!this.props.inlineEditor) {
      toolbarInline = false
      toolbarButtons = [
        'bold',
        'italic',
        'underline',
        '|',
        'formatOL',
        'formatUL',
        'outdent',
        'indent',
        'paragraphFormat',
        '|',
        'undo',
        'redo',
        'html'
      ]
      paragraphFormat = {
        N: 'Normal',
        PRE: 'Code'
      }
    }

    if (this.props.bShowLink) {
      toolbarButtons.push('insertLink')
    }

    if (this.props.fileUploadProps?.onUpload) {
      const { id } = this.props.fileUploadProps
      const uploadFileId = `upload-file-${id}`
      toolbarButtons.push(uploadFileId)
      this._registerUploadCommand(customToolbarButtons.uploadFile, this.props.fileUploadProps, uploadFileId)
    }

    return {
      enter: $.FroalaEditor.ENTER_DIV,
      codeMirror: false,
      placeholderText: this.props.placeholder
        ? IntlUtil.convertMessage(this.props.placeholder)
        : '',
      zIndex: 350,
      tabSpaces: 4,
      toolbarInline: toolbarInline,
      charCounterCount: false,
      heightMin: 'auto',
      toolbarButtons: toolbarButtons,
      paragraphFormat: paragraphFormat,
      toolbarSticky: this.props.inlineEditor,
      htmlAllowComments: false,
      linkAlwaysNoFollow: false,
      fontFamilySelection: false,
      fontSizeSelection: false,
      tooltips: false,
      events: {
        'froalaEditor.blur': this._onBlur.bind(this),
        'froalaEditor.commands.after': this._onCommandAfter.bind(this),
        'froalaEditor.initialized': this._onEditorInitialized.bind(this),
        'froalaEditor.editable.focus': this._onEditorFocus.bind(this),
        'froalaEditor.paste.beforeCleanup': this._onBeforeCleanup.bind(this),
        'froalaEditor.paste.afterCleanup': this._onAfterCleanup.bind(this)
      },
      htmlAllowedEmptyTags: ['pre']
    }
  }

  _registerUploadCommand = (uploadFileIcon, fileUploadProps, uploadFileId) => {
    const messages = IntlUtil.loadMessages()
    $.FroalaEditor.DefineIcon(uploadFileId, {
      SRC: uploadFileIcon,
      template: 'image'
    })
    $.FroalaEditor.RegisterCommand(uploadFileId, {
      type: 'dropdown',
      focus: false,
      undo: false,
      refreshAfterCallback: true,
      html: () => {
        // The current context is the editor instance.
        return `<div id="${uploadFileId}" class="upload-zone"></div>`
      },
      refreshOnShow: e =>
        this._renderUploadZone(e, messages, fileUploadProps, uploadFileId)
    })
  }

  _renderUploadZone = (
    [dropdownButton],
    messages,
    { onUpload, onDelete, id, uploadUrl, paramsToIncludeDuringUpload },
    uploadFileId
  ) => {
    const element = (
      <IntlProvider
        locale={IntlUtil.getIntlProviderLanguage()}
        messages={messages}
      >
        <PanelDoc
          key={id}
          uploadClassName={uploadFileId}
          uploadSuccess={(file, message) => onUpload(file, message)}
          onDelete={onDelete}
          showAttachments={false}
          uploadUrl={uploadUrl}
          paramsToIncludeDuringUpload={paramsToIncludeDuringUpload}
        />
      </IntlProvider>
    )
    ReactDom.render(
      element,
      dropdownButton.parentNode.querySelector(`#${uploadFileId}`)
    )
  }

  _onBeforeCleanup (e, editor, cmd) {
    if (cmd && typeof cmd === 'string') {
      return this._removeHtmlStyles(cmd)
    }
  }

  _onAfterCleanup (e, editor, cmd) {
    if (cmd && typeof cmd === 'string') {
      const html = this._removeHtmlStyles(cmd)
      let newHtml = ''
      const lines = html.split('<br>')

      lines.map(cont => {
        if (cont.trim() !== '') {
          newHtml += `<div>${cont}</div><br>`
        } else {
          newHtml += '<br>'
        }
      })

      return newHtml
    }
  }

  _removeHtmlStyles (html) {
    return html.replace(new RegExp(' style[ ]*=[ ]*"[^"]*"', 'g'), '')
  }

  _onCommandAfter (e, editor, cmd) {
    if (cmd === 'html' && this.props.handleCodeViewToggle) {
      this.props.handleCodeViewToggle(editor.codeView.isActive())
    }
  }

  _renderError () {
    if (!this.props.error) {
      return ''
    }

    return (
      <p className='error'>
        <IntlMessage ident={this.props.error} />
      </p>
    )
  }

  _onEditorInitialized (e, editor) {
    if (this.props.hasAutocomplete) {
      this._initAutocomplete(editor)
    }

    if (editor) {
      editor.events.on(
        'keydown',
        ev => {
          if (this.props.onKeyDown) {
            return this.props.onKeyDown(ev)
          } else if (
            ev.which === $.FroalaEditor.KEYCODE.ENTER &&
            editor.$el.atwho('isSelecting')
          ) {
            return false
          } else if (ev.shiftKey && ev.key === 'Enter') {
            ev.preventDefault()
            ev.stopPropagation()
            return false
          }
        },
        true
      )
    }
  }

  _onEditorFocus () {
    if (this.props.onFocus) {
      this.props.onFocus(this)
    }
  }

  _initAutocomplete (editor) {
    if (editor) {
      this._editorEl = editor

      const config = {
        at: this.props.autocompleteChar,
        data: this.props.autocompleteOptions,
        displayTpl: '<li>${value}</li>', // eslint-disable-line
        limit: 200
      }

      this._editorEl.$el.atwho(config).on('inserted.atwho', () => {
        this._editorEl.$el.find('.atwho-inserted').removeAttr('contenteditable')
      })
    }
  }
}

export default WysiwygEditor
